import Keycodes from '@constants/keycodes';

// Import required modular LoDash methods.
import _debounce from 'lodash/debounce';

// Use symbols for private attributes
const _events = Symbol('events');

export default class EventTracking {
    /**
     * User event tracking constructor.
     *
     * @constructor
     */
    constructor () {
        // This will be our primary user action log array.
        this[_events] = [];
    }

    /**
     * Getter for the tracked user events.
     *
     * @return {Array}
     */
    getEvents () {
        return this[_events];
    }

    /**
     * Initiate user event tracking.
     *
     * @return void
     */
    record () {
        // If error logging has been turned off then we have no need to track user actions so exit the constructor
        if (window._isJavaScriptErrorLoggingDisabled) {
            return;
        }

        // Store class as self for nested non-arrow function.
        const self = this;

        /**
         * Define our basic user action tracking event handler helper function.
         *
         * @param  {Object} event The native event object.
         */
        const eventHandler = event => {
            // Only continue logging a keypress event if it's the enter key that was pressed.
            if (event.type === 'keypress' && event.keyCode !== Keycodes.ENTER) {
                return;
            }

            // Add the event action to the logging user action array.
            this.pushEvent(event);
        };

        // Define our debounce configuration and options.
        // @link https://lodash.com/docs/4.17.4#debounce
        const debounceOptions = {
            rate: 500,
            options: {
                leading: true,
                trailing: false,
            },
        };

        // Store the original XMLHttpRequest open() method so we can override the original but still call the original logic.
        // @link https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/open
        XMLHttpRequest.prototype._open = XMLHttpRequest.prototype.open;

        // Override our prototypical XMLHttpRequest open() method to capture all XHR AJAX events and store the URL.
        // This is required because IE does not return responseURL attribute on the XMLHttpRequest
        // object (and also allows us to capture the request method)
        // @link https://msdn.microsoft.com/en-us/library/ms535874(v=vs.85).aspx
        XMLHttpRequest.prototype.open = function (method, url, async, user, password) {
            // Store request method and URL to a class variables.
            self.method = method;
            self.url = url;

            // Call the original XMLHttpRequest open() method that was saved to the _open() object method.
            this._open(method, url, true, user, password);
        };

        // Store the original XMLHttpRequest send() method so we can override the original but still call the original logic.
        XMLHttpRequest.prototype._send = XMLHttpRequest.prototype.send;

        // Override our prototypical XMLHttpRequest send() method to capture all XHR AJAX events and track the event.
        // @link https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/send
        XMLHttpRequest.prototype.send = function (data) {
            // Listen for the 'loadend' event and debounce the tracking capture.
            this.addEventListener('loadend', function () {
                // Add the event action to the logging user action array.
                self.xhrPushEvent(self, this);
            }, false);

            // Call the original XMLHttpRequest send() method that was saved to the _send() object method.
            this._send(data);
        };

        // To catch all events while still performing a debounce we must declare these handlers separately.
        const listeners = ['click', 'touch', 'focus', 'submit', 'keypress'];

        listeners.forEach(event => {
            document.documentElement.addEventListener(event, _debounce(eventHandler, debounceOptions.rate, debounceOptions.options));
        });
    }

    /**
     * Add the action to the class action logging array.
     *
     * @param UserEventRecording self    The UserEventRecording class instance.
     * @param Object             element The jQuery element object.
     * @param Object             event   The jQuery.Event object.
     */
    pushEvent (event) {
        // Get the action event's element path and define the event type.
        const composedPath = event.path || (event.composedPath && event.composedPath()) || this.composePath(event.target);
        const path = this.getElementDOMPath(composedPath);
        let eventType = event.type.toUpperCase();

        // If the event type is a keypress then clarify it's an action on the "Enter" key.
        if (eventType === 'KEYPRESS') {
            eventType += ' [ENTER]';
        }

        // If we have a DOM path for the element then add the action to the action logging array along with the timestamp and event type.
        if (path) {
            this[_events].push({
                event: eventType,
                timestamp: Date.now(),
                element: path,
            });
        }
    }

    /**
     * Compose path polyfill for Firefox and other browsers that don't support event.path or event.composedPath().
     *
     * @link https://stackoverflow.com/a/45061945/2221024
     *
     * @param  Object element The target DOM element.
     *
     * @return Array
     */
    composePath (element) {
        // Define the empty path array for the composed path build
        const path = [];

        // Iterate through each element beginning with the most nested child
        while (element) {
            // Push the element into the path array
            path.push(element);

            // If the HTML DOM element has been reached then exit the iteration loop
            if (element.tagName === 'HTML') {
                return path;
            }

            // Update the element with parent element
            element = element.parentElement;
        }
    }

    /**
     * Add the XHR event action to the class action logging array.
     *
     * @param UserEventRecording self    The UserEventRecording class instance.
     * @param Object             element The XMLHttpRequest element object.
     */
    xhrPushEvent (trackingInstance, response) {
        // Append the XHR AJAX request action to the action logging array.
        trackingInstance[_events].push({
            event: 'XHR REQUEST',
            method: trackingInstance.method,
            url: response.responseURL || trackingInstance.url,
            status: response.status,
            timestamp: Date.now(),
        });

        // If the responding HTTP code is a 4xx or 5xx then throw a new error to log
        // this to the server except for unauthorized response codes (401 and 419).
        if ([4, 5].includes(Number.parseInt(response.status / 100)) && response.status !== 401 && response.status !== 419) {
            throw new Error(
                'XMLHttpRequest to ' + (response.responseURL || trackingInstance.url)
                + ' [' + trackingInstance.method.toUpperCase() + ': ' + response.status + ']'
            );
        }
    }

    /**
     * Parse a string representation of the element's DOM path.
     *
     * Example Response: body > div:nth-child(2)#container > div:nth-child(2)#shadow.is-clearfix
     *
     * @return String The element's DOM path.
     */
    getElementDOMPath (pathNodes) {
        let pathString;
        let hasFoundBodyNode;

        // Iterate through the provided element and the element's parent nodes to build the DOM path string.
        pathNodes.forEach(node => {
            // If node does not have a name then abort iteration.
            if (! node.nodeName) {
                return;
            }

            // Get the node name
            let name = node.nodeName.toLowerCase();

            // If we've reached the beyond the body node then abort iteration because they will always be in the path.
            // Note: We are iterating up the DOM tree from the target node.
            if (hasFoundBodyNode) {
                return;
            }

            // Note that the iteration has reached the DOM <body> node
            if (name === 'body') {
                hasFoundBodyNode = true;
            }

            let siblingCount = 0;
            let currentNodeSibingIndex = 1;

            // If the node does not have an ID then iterate through any of the parent's matching name/class
            // nodes so that we can define the current sibling location index in the returned path string.
            if (! node.id && node.parentNode) {
                const siblings = node.parentNode.children || [];

                for (let i = 0, l = siblings.length; i < l; i++) {
                    if (siblings[i].nodeName.toLowerCase() === name && siblings[i].className === node.className) {
                        siblingCount += 1;

                        if (siblings[i] === node) {
                            currentNodeSibingIndex = siblingCount;
                        }
                    }
                }
            }

            // Add the sibling index string if it's not an only matching child of the parent node
            if (siblingCount > 1) {
                name += ':nth-child(' + currentNodeSibingIndex + ')';
            }

            // Append the ID and classes if available.
            // Note: Use a RegEx whitespace replace because these will retain newlines
            name += node.id ? '#' + node.id.trim().replace(/\s+/g, '') : '';
            name += (node.className && typeof node.className === 'string') ? '.' + node.className.trim().replace(/\s+/g, '.') : '';

            // Prepend the current loop element to the DOM path string since we are iterating up the DOM tree.
            pathString = name + (pathString ? ' > ' + pathString : '');
        });

        // Return the DOM tree path string.
        return pathString;
    }
}
